import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api, registerPdfPreviewer } from "../lib/api";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Loader2, FileText, Printer, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";

const Ctx = createContext({ previewPdf: () => {} });
export const usePdfPreview = () => useContext(Ctx);

const MIN_ZOOM = 50;
const MAX_ZOOM = 200;
const ZOOM_STEP = 25;

export function PdfPreviewProvider({ children }) {
  const [state, setState] = useState(null); // { url, filename, title, loading }
  const [zoom, setZoom] = useState(100);

  const previewPdf = useCallback(async (path, filename, title) => {
    const fname = filename || "document.pdf";
    const ttl = title || filename || "Document preview";
    setZoom(100);
    setState({ url: null, filename: fname, title: ttl, loading: true });
    try {
      const { data } = await api.get(path, { responseType: "blob", withCredentials: true });
      const blob = data.type === "application/pdf" ? data : new Blob([data], { type: "application/pdf" });
      setState({ url: URL.createObjectURL(blob), filename: fname, title: ttl, loading: false });
    } catch {
      toast.error("Could not load the PDF preview.");
      setState(null);
    }
  }, []);

  useEffect(() => { registerPdfPreviewer(previewPdf); }, [previewPdf]);

  const close = () => setState((s) => { if (s?.url) URL.revokeObjectURL(s.url); return null; });
  const download = () => {
    if (!state?.url) return;
    const a = document.createElement("a");
    a.href = state.url; a.download = state.filename;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const printPdf = () => {
    if (!state?.url) return;
    const frame = document.createElement("iframe");
    frame.style.position = "fixed"; frame.style.right = "0"; frame.style.bottom = "0";
    frame.style.width = "0"; frame.style.height = "0"; frame.style.border = "0";
    frame.src = state.url;
    frame.onload = () => {
      try { frame.contentWindow.focus(); frame.contentWindow.print(); }
      catch { window.open(state.url, "_blank", "noopener"); }
      setTimeout(() => frame.remove(), 60000);
    };
    document.body.appendChild(frame);
  };
  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP));
  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP));
  const frameSrc = state?.url ? `${state.url}#zoom=${zoom}&toolbar=0&navpanes=0` : "";

  return (
    <Ctx.Provider value={{ previewPdf }}>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-5xl sm:max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col gap-0" data-testid="pdf-preview-dialog">
          <DialogTitle className="sr-only">{state?.title || "PDF preview"}</DialogTitle>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50 pr-12 shrink-0">
            <FileText className="h-4 w-4 text-[#1E3A8A] shrink-0" />
            <span className="text-sm font-semibold text-slate-700 truncate flex-1" data-testid="pdf-preview-title">{state?.title}</span>
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1 py-0.5 mr-1">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={zoomOut} disabled={!state?.url || zoom <= MIN_ZOOM} data-testid="pdf-preview-zoom-out" title="Zoom out"><ZoomOut className="h-4 w-4" /></Button>
              <span className="text-xs font-semibold text-slate-600 w-10 text-center tabular-nums" data-testid="pdf-preview-zoom-level">{zoom}%</span>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={zoomIn} disabled={!state?.url || zoom >= MAX_ZOOM} data-testid="pdf-preview-zoom-in" title="Zoom in"><ZoomIn className="h-4 w-4" /></Button>
            </div>
            <Button size="sm" variant="outline" onClick={printPdf} disabled={!state?.url} data-testid="pdf-preview-print"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
            <Button size="sm" onClick={download} disabled={!state?.url} data-testid="pdf-preview-download" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Download className="h-4 w-4 mr-1.5" /> Download</Button>
          </div>
          <div className="flex-1 bg-slate-100 min-h-0">
            {state?.loading ? (
              <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading preview…</div>
            ) : state?.url ? (
              <iframe key={zoom} title="PDF preview" src={frameSrc} className="w-full h-full border-0" data-testid="pdf-preview-frame" />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}
